// src/pages/team/ReengagementReview.jsx
//
// Re-engagement Review screen — shows the disengaged parent list +
// auto-drafted English message preview + Send CTA. Approved spec:
// features/disengaged-parent/02-wireframe.md Screen 2 +
// 03-architecture.md §5.1.
//
// Flow:
//   1. Mount: call getDisengagedParents to refetch the list (server
//      is the source of truth — never trust a list passed via
//      navigation state).
//   2. Coach reviews list + message preview (English-only preview;
//      Spanish parents get Spanish per F3 / D8).
//   3. Tap Send: generate a UUID idempotency key, call
//      sendReengagementMessages, return to Team Detail with toast.
//
// Empty / error / sending states all covered per wireframe §3.

import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ArrowLeftIcon, PaperAirplaneIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import RecipientListItem from '@/components/team/RecipientListItem';
import { useAuth } from '@/context/AuthContext';

const HEAD_FONT = "'Barlow Condensed', sans-serif";

/**
 * Build the English message preview the coach sees, using their own
 * first name for the {coachName} slot. This is the same template that
 * the server will send for English-language recipients; Spanish
 * recipients receive a Spanish translation per spec D8.
 */
function buildEnglishPreview(coachFirstName) {
  return `Hi {parentName}, Coach ${coachFirstName} here. Haven't seen you in Scrimly lately — open the app or reply anytime.`;
}

export default function ReengagementReview() {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { userProfile, firebaseUser } = useAuth();

  const [state, setState] = useState({ status: 'loading', recipients: [], error: null });
  const [sending, setSending] = useState(false);

  // Server is source of truth — refetch on mount, never trust nav state.
  useEffect(() => {
    if (!teamId) return;
    let cancelled = false;
    (async () => {
      try {
        const fns = getFunctions(undefined, 'us-central1');
        const callable = httpsCallable(fns, 'getDisengagedParents');
        const res = await callable({ teamId });
        if (cancelled) return;
        const list = Array.isArray(res?.data?.disengaged) ? res.data.disengaged : [];
        setState({ status: list.length ? 'ready' : 'empty', recipients: list, error: null });
      } catch (err) {
        if (!cancelled) {
          setState({
            status: 'error',
            recipients: [],
            error: err?.message || 'unknown',
          });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [teamId]);

  // Pull coach first name from the profile context. The SERVER does
  // the canonical interpolation; this is just the preview the coach
  // sees on screen.
  const coachFirstName = useMemo(() => {
    const fromProfile = userProfile?.firstName && String(userProfile.firstName).trim();
    if (fromProfile) return fromProfile;
    const dn = userProfile?.displayName || firebaseUser?.displayName || '';
    return String(dn).trim().split(/\s+/)[0] || 'Coach';
  }, [userProfile, firebaseUser]);

  const hasSpanishRecipient = state.recipients.some((r) => r.language === 'es');

  const handleSend = async () => {
    if (sending) return;
    setSending(true);
    try {
      // Idempotency: a fresh UUID per Send tap. Double-tap protection
      // is twofold — server scopes the key to a single result, and
      // we disable the button while in-flight.
      const idempotencyKey =
        (globalThis.crypto && typeof globalThis.crypto.randomUUID === 'function')
          ? globalThis.crypto.randomUUID()
          : `client-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

      const fns = getFunctions(undefined, 'us-central1');
      const callable = httpsCallable(fns, 'sendReengagementMessages');
      const res = await callable({ teamId, idempotencyKey });
      const data = res?.data || {};
      const sent = Number(data.sent) || 0;
      const failed = Number(data.failed) || 0;
      const total = sent + failed;

      // Toast variants per WD8.
      if (total === 0) {
        toast.error(t('smartActions.disengagedParent.toastAllFailed'));
      } else if (failed === 0) {
        toast.success(
          `${t('smartActions.disengagedParent.toastAllSent', { count: sent })}\n${t('smartActions.disengagedParent.toastSubtext')}`,
          { duration: 4000 }
        );
      } else if (sent === 0) {
        toast.error(t('smartActions.disengagedParent.toastAllFailed'), { duration: 6000 });
      } else {
        toast(
          t('smartActions.disengagedParent.toastPartial', { sent, total, failed }),
          { duration: 6000, icon: '⚠️' }
        );
      }
      navigate(`/teams/${teamId}`);
    } catch (err) {
      toast.error(t('smartActions.disengagedParent.toastAllFailed'));
      setSending(false);
    }
  };

  /* ───── Render ──────────────────────────────────────────── */

  return (
    <div className="min-h-screen bg-[#f7faf5]">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <button
          type="button"
          onClick={() => navigate(`/teams/${teamId}`)}
          className="inline-flex items-center gap-1.5 mb-4 text-sm text-zinc-600 hover:text-[#0d1f14]"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          {t('smartActions.disengagedParent.reviewCancel')}
        </button>

        <h1
          className="text-2xl sm:text-3xl font-bold uppercase tracking-tight text-[#0d1f14] mb-1"
          style={{ fontFamily: HEAD_FONT }}
        >
          {t('smartActions.disengagedParent.reviewTitle')}
        </h1>

        {state.status === 'loading' && (
          <div className="mt-6 space-y-3">
            <div className="h-4 w-40 bg-zinc-200 rounded animate-pulse" />
            <div className="rounded-2xl border border-zinc-200 bg-white p-4 space-y-3">
              {[0, 1, 2].map((i) => (
                <div key={i} className="flex items-center gap-3 animate-pulse">
                  <div className="flex-1 min-w-0">
                    <div className="h-3 w-1/2 bg-zinc-200 rounded mb-1.5" />
                    <div className="h-2.5 w-1/3 bg-zinc-100 rounded" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {state.status === 'error' && (
          <div className="mt-6 rounded-xl border border-red-200 bg-red-50 p-4">
            <p className="text-sm font-semibold text-red-700">
              {t('smartActions.disengagedParent.toastAllFailed')}
            </p>
          </div>
        )}

        {state.status === 'empty' && (
          <div className="mt-6 rounded-xl border border-zinc-200 bg-white p-6 text-center">
            <p className="text-sm text-zinc-600">
              {t('smartActions.disengagedParent.toastAllSent', { count: 0 })}
            </p>
            <button
              type="button"
              onClick={() => navigate(`/teams/${teamId}`)}
              className="mt-4 inline-flex items-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-4 py-2 text-sm font-bold uppercase"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
            >
              <ArrowLeftIcon className="h-4 w-4" />
              {t('smartActions.disengagedParent.reviewCancel')}
            </button>
          </div>
        )}

        {state.status === 'ready' && (
          <>
            <p className="mt-1 text-sm text-zinc-600 mb-4">
              {t('smartActions.disengagedParent.reviewSendingTo', { count: state.recipients.length })}
            </p>

            {/* Recipient list */}
            <div className="rounded-2xl border border-zinc-200 bg-white overflow-hidden mb-5">
              {state.recipients.map((r) => (
                <RecipientListItem
                  key={r.uid}
                  displayName={r.displayName}
                  phoneMasked={r.phoneMasked}
                  language={r.language}
                />
              ))}
            </div>

            {/* Message preview */}
            <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2"
               style={{ fontFamily: HEAD_FONT, letterSpacing: '1px' }}>
              {t('smartActions.disengagedParent.reviewMessageLabel')}
            </p>
            <div className="rounded-2xl border border-zinc-200 bg-white p-4 mb-3">
              <p className="text-sm text-zinc-800 leading-relaxed">
                {buildEnglishPreview(coachFirstName)}
              </p>
            </div>

            {hasSpanishRecipient && (
              <p className="flex items-start gap-1.5 text-xs text-zinc-500 mb-6">
                <InformationCircleIcon className="h-4 w-4 flex-shrink-0 text-[#5a8a1f] mt-0.5" />
                <span>{t('smartActions.disengagedParent.reviewSpanishNote')}</span>
              </p>
            )}

            {/* Send button */}
            <button
              type="button"
              onClick={handleSend}
              disabled={sending}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-6 py-3 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97] disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
            >
              <PaperAirplaneIcon className="h-4 w-4" />
              {sending ? '…' : t('smartActions.disengagedParent.reviewSendButton')}
            </button>

            <button
              type="button"
              onClick={() => navigate(`/teams/${teamId}`)}
              disabled={sending}
              className="block mt-4 text-sm text-zinc-500 hover:text-[#0d1f14] disabled:opacity-60"
            >
              {t('smartActions.disengagedParent.reviewCancel')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
